import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, FileText, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";

const documents = [
  {
    title: "Declaration",
    description:
      "The official declaration of The Kingdom of Yunah'Shach'Qal Yashar'al, its purpose, mission, and covenant witness.",
    type: "PDF",
    size: "Official Document",
    downloadUrl: "/documents/Micronation-Declaration-form-Yahsharal.pdf",
    internalLink: "/governance/declaration",
  },
  {
    title: "Constitution & Internal Covenant",
    description:
      "The Constitution and Internal Covenant establishing the governing order, national standards, and covenant responsibilities.",
    type: "PDF",
    size: "Official Document",
    downloadUrl: "/documents/constitution.pdf",
    internalLink: "/governance/constitution",
  },
];

export default function OfficialDocuments() {
  return (
    <div className="min-h-screen" data-testid="official-documents-page">
      {/* Hero */}
      <section className="bg-royal-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/governance"
            className="inline-flex items-center text-slate-400 hover:text-white mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Governance
          </Link>

          <h1 className="heading-hero">Official Documents Library</h1>

          <p className="text-slate-300 mt-4">
            Read and download official governance documents, records, and public materials of The Kingdom.
          </p>
        </div>
      </section>

      {/* Document Integrity Note */}
      <section className="bg-parchment py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border-l-4 border-kingdom-gold p-4 rounded-sm">
            <p className="text-sm text-slate-700">
              <strong>Document Integrity Note:</strong> The online pages are formatted for website readability. The downloadable PDFs are the official document files stored locally with this website.
            </p>
          </div>
        </div>
      </section>

      {/* Documents Grid */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-section text-royal-blue mb-8">
            Available Documents
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            {documents.map((doc) => (
              <Card key={doc.title} className="card-official">
                <CardHeader>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-kingdom-gold/10 rounded flex items-center justify-center flex-shrink-0">
                      <FileText className="w-6 h-6 text-kingdom-gold" />
                    </div>

                    <div>
                      <CardTitle className="heading-card text-royal-blue">
                        {doc.title}
                      </CardTitle>

                      <p className="text-sm text-slate-500 mt-1">
                        {doc.type} • {doc.size}
                      </p>
                    </div>
                  </div>
                </CardHeader>

                <CardContent>
                  <p className="text-slate-600 mb-4">
                    {doc.description}
                  </p>

                  <div className="flex flex-wrap gap-3">
                    <Link to={doc.internalLink}>
                      <Button
                        variant="outline"
                        size="sm"
                        className="btn-outline-royal"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Read Online
                      </Button>
                    </Link>

                    <a href={doc.downloadUrl} download>
                      <Button size="sm" className="btn-gold">
                        <Download className="w-4 h-4 mr-2" />
                        Download PDF
                      </Button>
                    </a>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Resources */}
      <section className="bg-parchment py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-section text-royal-blue mb-8">
            External Legal Resources
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <a
              href="https://www.law.cornell.edu/uscode/text/28/1691"
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-white p-6 rounded-sm shadow hover:shadow-lg transition-shadow"
            >
              <h3 className="font-heading text-lg font-bold text-royal-blue mb-2">
                28 U.S.C. § 1691
              </h3>

              <p className="text-slate-600 text-sm">
                Seal requirement for court writs and processes.
              </p>

              <span className="text-kingdom-gold text-sm mt-2 inline-flex items-center gap-1">
                View on Cornell Law <ExternalLink className="w-3 h-3" />
              </span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
